/**
 * Back returns focus to what opened the screen it closes — never to nothing.
 *
 * WHY. The owner's rule: Back closes the top layer and returns focus to what
 * opened it. Two Backs broke it (nav audit on the onn box, 2026-10-05): from
 * the guide to Home, and out of a full-screen film. Both left focus on the
 * page body, with no ring anywhere, until the next press happened to recover.
 * The control that had focus was not lost by anyone's choice: the page it was
 * on was rebuilt (Home remounts when the router comes back to it) or hidden
 * (the player), and the browser drops focus to <body> when that happens.
 *
 * HOW. Every history entry has an `idx` — the router's, and the back stack's
 * layers carry it forward (packages/ui backStack.ts). While the viewer moves
 * around, this remembers which control had focus on each entry. When Back
 * lands on an entry and focus is on nothing, it puts focus back on the control
 * remembered for that entry, waiting briefly for the page to draw it (Home's
 * rows arrive after the route renders). If that control is gone, the page's
 * own item in the nav rail. Anything that takes focus by itself first — a
 * dialog, the guide restoring its channel — wins, and this does nothing.
 */

/** How long a page may take to draw the control it is getting back. */
const WAIT_MAX_MS = 2_500;
const POLL_MS = 100;

/** CSS.escape, with a stand-in where it is missing (old WebViews, the test DOM). */
const esc = (value: string): string =>
  typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(value) : value.replace(/["\\]/g, "\\$&");

/** How to find this control again on a rebuilt page. */
export function locatorFor(el: Element): string | null {
  if (!(el instanceof HTMLElement) || el === document.body) return null;
  const { homeRow, homeCol } = el.dataset;
  if (homeRow !== undefined && homeCol !== undefined) return `[data-home-row="${homeRow}"][data-home-col="${homeCol}"]`;
  const link = el.closest(".navrail a[href]");
  if (link) return `.navrail a[href="${esc(link.getAttribute("href") ?? "")}"]`;
  if (el.id) return `#${esc(el.id)}`;
  const label = el.getAttribute("aria-label");
  if (label) return `${el.tagName.toLowerCase()}[aria-label="${esc(label)}"]`;
  // A path from the app root: good enough for a page rebuilt with the same shape.
  const parts: string[] = [];
  for (let node: Element | null = el; node && node.id !== "root" && node !== document.body; node = node.parentElement) {
    const parent: Element | null = node.parentElement;
    if (!parent) return null;
    parts.unshift(`${node.tagName.toLowerCase()}:nth-child(${Array.prototype.indexOf.call(parent.children, node) + 1})`);
  }
  return parts.length ? `#root > ${parts.join(" > ")}` : null;
}

function visible(el: Element | null): el is HTMLElement {
  // A faded-out player bar still has a box: its buttons are not visible.
  return el instanceof HTMLElement && el.isConnected && el.getClientRects().length > 0 && !el.closest(".player-surface--idle");
}

/**
 * Full screen with the bar away: nothing focused is right there — Up/Down change
 * the channel. Closing the Recent channels strip as the bar went away put the
 * ring back on the hidden ▤ (nav audit, onn box, 2026-10-06), and the nav rail
 * fallback is behind the picture.
 */
function watching(): boolean {
  return document.querySelector(".player-surface--fullscreen.player-surface--idle") !== null;
}

function find(selector: string | undefined): HTMLElement | null {
  if (!selector) return null;
  try {
    const el = document.querySelector(selector);
    return visible(el) ? el : null;
  } catch {
    return null;
  }
}

function nothingFocused(): boolean {
  const active = document.activeElement;
  return !active || active === document.body || active === document.documentElement || !active.isConnected;
}

function entryIdx(): number {
  const idx = (history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === "number" ? idx : 0;
}

/** Install on the window. Returns the function to remove it. */
export function installFocusReturn(): () => void {
  const remembered = new Map<number, string>();
  let timer: ReturnType<typeof setTimeout> | null = null;

  const stop = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  const onFocusIn = (event: FocusEvent) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target === document.body) return;
    // Something took focus by itself: nothing left to give back.
    stop();
    const where = locatorFor(target);
    if (where) remembered.set(entryIdx(), where);
  };

  const onPopState = () => {
    stop();
    const idx = entryIdx();
    const started = Date.now();
    const tryRestore = () => {
      timer = null;
      if (!nothingFocused() || watching()) return;
      const el = find(remembered.get(idx));
      if (el) {
        el.focus();
        return;
      }
      if (Date.now() - started < WAIT_MAX_MS) {
        timer = setTimeout(tryRestore, POLL_MS);
        return;
      }
      // The control is gone: the page's own place in the nav rail, never nothing.
      const rail = find(`.navrail a[href="${esc(location.pathname)}"]`) ?? find(".navrail a[href]");
      rail?.focus();
    };
    // After the router and the closing layer have rendered.
    timer = setTimeout(tryRestore, POLL_MS);
  };

  document.addEventListener("focusin", onFocusIn);
  window.addEventListener("popstate", onPopState);
  return () => {
    stop();
    document.removeEventListener("focusin", onFocusIn);
    window.removeEventListener("popstate", onPopState);
  };
}
